import { useState, useCallback } from 'react';
import { proyectoClienteApi } from '../api/proyectoClienteApi';
import { showErrorToast, showSuccessToast } from '../utils/errorHandler';

/**
 * Hook personalizado para gestión de asignaciones Proyecto-Cliente
 * Proporciona estado y funciones para operaciones de la tabla pivot proyectos_clientes
 */
export const useProyectoCliente = () => {
  const [proyectosAsignados, setProyectosAsignados] = useState([]);
  const [clientesAsignados, setClientesAsignados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Obtiene los proyectos asignados a un cliente específico
   */
  const fetchProyectosByCliente = useCallback(async (clienteId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await proyectoClienteApi.getProyectosByCliente(clienteId);
      setProyectosAsignados(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar proyectos del cliente');
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene los clientes asignados a un proyecto específico
   */
  const fetchClientesByProyecto = useCallback(async (proyectoId) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Hook: Llamando API para obtener clientes del proyecto:', proyectoId);
      const response = await proyectoClienteApi.getClientesByProyecto(proyectoId);
      console.log('Hook: Respuesta de la API:', response);
      console.log('Hook: Datos de clientes recibidos:', response.data);
      
      setClientesAsignados(response.data); // Usar estado específico para clientes
      setProyectosAsignados(response.data); // Mantener compatibilidad con uso existente
      
      console.log('Hook: Estado actualizado con clientes');
      return response.data;
    } catch (err) {
      console.error('Hook: Error al obtener clientes:', err);
      const errorMessage = showErrorToast(err, 'Error al cargar clientes del proyecto');
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Asigna un proyecto a un cliente (también funciona para asignar cliente a proyecto)
   */
  const asignarProyecto = useCallback(async (clienteId, proyectoId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await proyectoClienteApi.asignarProyecto({
        id_cliente: clienteId,
        id_proyecto: proyectoId
      });
      
      showSuccessToast('Cliente asignado exitosamente');
      
      // Actualizar la lista local - puede ser proyectos por cliente o clientes por proyecto
      // dependiendo del contexto de uso
      
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al asignar cliente');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Desasigna un proyecto de un cliente (también funciona para desasignar cliente de proyecto)
   */
  const desasignarProyecto = useCallback(async (clienteId, proyectoId) => {
    setLoading(true);
    setError(null);
    
    try {
      await proyectoClienteApi.desasignarProyecto({
        id_cliente: clienteId,
        id_proyecto: proyectoId
      });
      
      showSuccessToast('Cliente desasignado exitosamente');
      
      return true;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al desasignar cliente');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Manejo local de estado para agregar proyecto (para clientes nuevos)
   */
  const agregarProyectoLocal = useCallback((proyecto) => {
    setProyectosAsignados(prev => {
      const yaExiste = prev.some(p => p.id === proyecto.id);
      if (yaExiste) {
        showErrorToast(null, 'El proyecto ya está asignado');
        return prev;
      }
      return [...prev, proyecto];
    });
  }, []);

  /**
   * Manejo local de estado para eliminar proyecto (para clientes nuevos)
   */
  const eliminarProyectoLocal = useCallback((proyectoId) => {
    setProyectosAsignados(prev => prev.filter(p => p.id !== proyectoId));
  }, []);

  /**
   * Limpia el estado local de proyectos asignados
   */
  const limpiarProyectosAsignados = useCallback(() => {
    setProyectosAsignados([]);
  }, []);

  return {
    // Estado
    proyectosAsignados,
    clientesAsignados,
    loading,
    error,
    
    // Funciones API
    fetchProyectosByCliente,
    fetchClientesByProyecto,
    asignarProyecto,
    desasignarProyecto,
    
    // Funciones locales (para clientes nuevos)
    agregarProyectoLocal,
    eliminarProyectoLocal,
    limpiarProyectosAsignados,
    
    // Función para limpiar errores
    clearError: () => setError(null),
  };
};

export default useProyectoCliente;
